import { Session } from 'next-auth';

import { JourneyType } from '@bytel/leviator';

import { getAppConfig } from '@services/config';
import { httpService } from '@services/http';
import { HttpService } from '@services/http/http';
import { faiStaticsData } from '@services/statics/fai';

import { getServerSession } from '@actions/get-session';

import { sapicRepository } from '@repositories/sapic';

import { SapicCategoryId, SapicFundingMode } from '@app-types/api/sapic';
import { ProductsItem } from '@app-types/api/statics';
import { DiscountType } from '@app-types/discount';
import { FaiType } from '@app-types/plan';
import { SimulatorCart } from '@app-types/simulator/virtual-cart';

import { isServer } from '@helpers/config';
import { localApiPaths } from '@helpers/path';

import { CATEGORY_TYPE } from '@constants/provider';

const appConfig = getAppConfig();

interface GetFaiOffersType {
    fundingMode?: SapicFundingMode;
    limit?: number;
    filterByTypes?: SapicCategoryId[];
    category?: SapicCategoryId;
    gencodes?: string[];
    excludeCategoryType?: CATEGORY_TYPE;
}

class FaiService {
    private httpService: HttpService;
    private readonly defaultMobileOffer = appConfig.defaultPlans.simulator.mobile;

    public constructor(httpService: HttpService) {
        this.httpService = httpService;
    }

    public async getOffers(
        options: GetFaiOffersType,
        cart?: { faiGencodes: string[]; mobileGencodes: string[] },
        shouldBeProspect: boolean = false,
        _session?: Session,
        isPostEligibility: boolean = false,
    ): Promise<FaiType[]> {
        const session = shouldBeProspect ? null : (_session ?? (await getServerSession()));

        if (isServer()) {
            return this.getOffersServer(options, session, cart, isPostEligibility);
        }

        const headers: Record<string, string> = {};
        if (session?.user) {
            headers['Authorization'] = `Bearer ${session.user.accessToken}`;
        }

        return this.httpService.get<FaiType[]>(
            localApiPaths.fai,
            {
                shouldBeProspect,
                cart: JSON.stringify(cart),
                options: JSON.stringify(options),
                isPostEligibility,
            },
            {
                headers,
            },
        );
    }

    private async getOffersServer(
        options: GetFaiOffersType,
        session: Session | null,
        cart?: { faiGencodes: string[]; mobileGencodes: string[] },
        isPostEligibility: boolean = false,
    ): Promise<FaiType[]> {
        const staticsDataPromise = faiStaticsData();

        let offers: FaiType[];

        if (session?.user || cart) {
            const faiProducts = cart?.faiGencodes ?? [];
            const mobileProducts = cart?.mobileGencodes ?? undefined;
            const virtualCart = this.buildVirtualCart(faiProducts, mobileProducts);

            try {
                const response = await sapicRepository.getFaiPlans(virtualCart, options, session);
                offers = response.products;
            } catch {
                offers = [];
            }
        } else {
            offers = await this.getProspectOffersWithDiscounts(options);
        }

        const staticsData = await staticsDataPromise;
        return this.getProductWithCmsInfo(offers, staticsData, isPostEligibility);
    }

    private async getProspectOffersWithDiscounts(options: GetFaiOffersType): Promise<FaiType[]> {
        const prospectVirtualCart = this.buildVirtualCart([], []);
        const simulatedVirtualCart = this.buildVirtualCart([], [this.defaultMobileOffer]);

        const [prospectResult, simulatedResult] = await Promise.allSettled([
            sapicRepository.getFaiPlans(prospectVirtualCart, options),
            sapicRepository.getFaiPlans(simulatedVirtualCart, options),
        ]);

        if (prospectResult.status === 'rejected') {
            console.error('Failed to fetch prospect offers:', prospectResult.reason);
            return [];
        }

        const prospectOffers = prospectResult.value.products;

        if (simulatedResult.status === 'rejected') {
            console.warn('Failed to fetch simulated offers, returning prospect offers without incitation discounts');
            return prospectOffers;
        }

        const simulatedOffers = simulatedResult.value.products;

        const discountsMap = new Map<string, DiscountType[]>(
            simulatedOffers.map((offer) => [offer.gencode, offer.details.discounts]),
        );

        return prospectOffers.map((prospectOffer) => {
            const simulatedDiscounts = (discountsMap.get(prospectOffer.gencode) ?? []).map((discount) => ({
                ...discount,
                isIncitation: true,
            }));

            return {
                ...prospectOffer,
                details: {
                    ...prospectOffer.details,
                    discounts: [...prospectOffer.details.discounts, ...simulatedDiscounts],
                },
            };
        });
    }

    private buildVirtualCart(faiProducts: string[], mobileProducts?: string[]): SimulatorCart {
        const cart: SimulatorCart = {
            cart: {
                quotes: [
                    {
                        type: JourneyType.FAI_ACQUISITION,
                        isCurrent: true,
                        products: faiProducts.map((product) => ({ gencode: product })),
                    },
                ],
            },
        };

        if (mobileProducts) {
            cart.cart.quotes.push({
                type: JourneyType.MOBILE_ACQUISITION,
                isCurrent: false,
                products: mobileProducts.map((product) => ({ gencode: product })),
            });
        }

        return cart;
    }

    private getProductWithCmsInfo(
        products: FaiType[],
        staticsData: ProductsItem[],
        isPostEligibility: boolean,
    ): FaiType[] {
        return products.map((product) => {
            const slug = `${product.rangeNg}_${product.commercialSeries}_${product.technology?.toLocaleLowerCase()}`;
            const data = staticsData.find((staticData) => {
                const staticSlug =
                    `${staticData.range_ng}_${staticData.commercial_series}_${staticData.techno}`.toLowerCase();

                return staticSlug === slug;
            });

            if (data) {
                product.name = data.title || product.name;
                product.image = data.image_url || product.image;
                product.subtitle = data.subtitle || '';
                product.detailsUrl = data.details_url;
                // Use VariantState.MAIN to display the sticker when trilogy fix server component
                product.stickers = [{ label: data.sticker || '', color: data.sticker_color ?? 'MAIN' }];
                product.hat = data.hat ?? undefined;
                this.handleStaticsData(data, product, isPostEligibility);
            }
            return product;
        });
    }

    private handleStaticsData(data: ProductsItem, product: FaiType, isPostEligibility: boolean): void {
        if (data.nuggets) {
            product.nuggets = data.nuggets
                .map((nugget) => ({
                    icon: nugget.logo_nuggets,
                    image: nugget.logo_image,
                    label: nugget.texte,
                    description: nugget.texte,
                    position: Number(nugget.position),
                    showSpeed: nugget.show_speed,
                }))
                .sort((a, b) => {
                    const positionA = a.position ?? Number.MAX_SAFE_INTEGER;
                    const positionB = b.position ?? Number.MAX_SAFE_INTEGER;
                    return positionA - positionB;
                });
        }

        if (data.fai_options) {
            product.options = data.fai_options.items?.map((item) => ({
                label: item.label,
                logo_alt: item.logo_alt,
                logo_url: item.logo_url,
                position: Number(item.order),
            }));
        }

        if (!isPostEligibility && data.pre_elig_speed_down && data.pre_elig_speed_up) {
            product.upRates = `${data.pre_elig_speed_up}\u00A0Gb/s`;
            product.downRates = `${data.pre_elig_speed_down}\u00A0Gb/s`;
            product.speedWording = data.pre_elig_speed_wording;
        }

        if (data.comparator) {
            product.comparator = data.comparator.map((comp) => ({
                category: comp.comparator_category,
                item: comp.comparator_item,
                value: comp.comparator_item_value,
                booleanTrueValue: comp.comparator_value_boolean,
            }));
        }

        if (data.text_big) {
            product.bottomSheet = {
                icon: data.picto_trilogy_big,
                title: data.text_big,
            };
        }
    }
}

export const faiService = new FaiService(httpService);
